import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ProvenanceTag from "@/components/ProvenanceTag";
import { Swords, Loader2, Trophy, Target } from "lucide-react";
import { toast } from "sonner";

export default function CompetitorRadar() {
  const [query, setQuery] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!query.trim()) { toast("Enter a product or URL"); return; }
    setLoading(true); setData(null);
    try {
      const res = await api.post("/ai/competitor", { query, context });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4"><Swords className="w-6 h-6 text-red-400" /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50">Competitor Radar</h1>
        <p className="text-slate-400 mt-2 max-w-xl mx-auto">Map the competitive landscape and find angles to win.</p>
      </div>

      <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 mb-8 space-y-4">
        <div><Label className="text-slate-400 text-xs mb-1.5 block">Product name or URL</Label>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Jobber, or contractor proposal software" className="bg-white/5 border-white/10 text-slate-200" data-testid="competitor-query-input" /></div>
        <div><Label className="text-slate-400 text-xs mb-1.5 block">Context (optional)</Label>
          <Textarea value={context} onChange={(e) => setContext(e.target.value)} placeholder="Your positioning or target market" className="bg-white/5 border-white/10 text-slate-200" /></div>
        <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-red-500 to-orange-500 text-slate-950 font-semibold" data-testid="competitor-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Mapping competitors…</> : <><Target className="w-4 h-4 mr-2" />Analyze Competition</>}
        </Button>
      </form>

      {data && (
        <div className="space-y-6 fade-up">
          <div className="glass rounded-xl p-5"><ProvenanceTag type="ai" /><p className="text-slate-300 mt-3">{data.market_summary}</p></div>

          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/8 text-slate-500">
                <th className="text-left p-4">Competitor</th><th className="text-left p-4">Positioning</th><th className="text-left p-4">Pricing</th><th className="text-left p-4">Weaknesses</th>
              </tr></thead>
              <tbody>
                {(data.competitors || []).map((c, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-4 font-semibold text-slate-100">{c.name}<p className="text-xs text-slate-500 font-normal mt-0.5">{c.target}</p></td>
                    <td className="p-4 text-slate-300">{c.positioning}</td>
                    <td className="p-4 font-mono text-cyan-400 text-xs">{c.pricing}</td>
                    <td className="p-4"><ul className="space-y-1">{(c.weaknesses || []).map((w, j) => <li key={j} className="text-xs text-slate-400">• {w}</li>)}</ul></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass rounded-xl p-5"><div className="flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-cyan-400" /><h4 className="font-semibold text-slate-100">Differentiation opportunities</h4></div>
              <ul className="space-y-1.5">{(data.differentiation_opportunities || []).map((d, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-cyan-500 mt-1">▸</span>{d}</li>)}</ul></div>
            <div className="glass rounded-xl p-5 glow-cyan"><div className="flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-emerald-400" /><h4 className="font-semibold text-slate-100">How could you beat them?</h4></div>
              <ul className="space-y-1.5">{(data.how_to_beat_them || []).map((d, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-emerald-500 mt-1">▸</span>{d}</li>)}</ul></div>
          </div>
        </div>
      )}
    </div>
  );
}
